/**
 * Phase 1 — public Library storefront (`/library`). Mock data only; no ownership APIs.
 * @typedef {"all" | "nft_comics" | "digital_comics" | "books" | "coming_soon"} LibraryTabId
 */

export const LIBRARY_TABS = [
  { id: "all", label: "All" },
  { id: "nft_comics", label: "Collector Editions" },
  { id: "digital_comics", label: "Digital Comics" },
  { id: "books", label: "Books" },
  { id: "coming_soon", label: "Coming Soon" },
];

/** Four-step explainer: NFT vs interior comic vs viewer. */
export const LIBRARY_HOW_STEPS = [
  {
    step: 1,
    title: "Purchase Collector Edition",
    copy: "Choose your tier and complete secure checkout through the official Treydora release.",
  },
  {
    step: 2,
    title: "Unique IPFS Cover Art",
    copy: "Your edition carries cover artwork in IPFS-linked metadata — often one combined piece rather than separate files.",
  },
  {
    step: 3,
    title: "Open Comic Viewer",
    copy: "The full interior is hosted inside Treydora — unlocked in your account after purchase.",
  },
  {
    step: 4,
    title: "Read the Complete Volume",
    copy: "Collector cover → interior pages in Treydora → Storyteller card for lore beside your read.",
  },
];

export const LIBRARY_NFT_TIERS = [
  {
    id: "merch",
    tierKey: "merch_rights",
    name: "Merch Rights Edition",
    supplyLabel: "FIRST 5,000",
    tierRank: "highest",
    imageFile: "04_library_featured_display.png",
    tagline: "Flagship collector tier with commercial voice.",
    benefits: [
      "Merchandising rights on approved Treydora Genesis assets",
      "Full comic reader access",
      "Treydora & Oshar universe access",
    ],
    ctaDetails: "View Details",
    ctaBuy: "Edition Coming Soon",
    tabIds: ["all", "nft_comics"],
  },
  {
    id: "armory",
    tierKey: "armory",
    name: "Armory Edition",
    supplyLabel: "NEXT 5,000",
    tierRank: "mid",
    imageFile: "02_library_tomes_reader_background.png",
    tagline: "Battle-ready rarity with vault-grade presentation.",
    benefits: [
      "Rare weapon or armor unlock (roadmap)",
      "Full comic reader access",
      "Treydora & Oshar universe access",
    ],
    ctaDetails: "View Details",
    ctaBuy: "Edition Coming Soon",
    tabIds: ["all", "nft_comics"],
  },
  {
    id: "reader",
    tierKey: "reader_access",
    name: "Reader Access Edition",
    supplyLabel: "ENTRY TIER",
    tierRank: "entry",
    imageFile: "00_library_page_master_mockup.png",
    tagline: "The essential on-ramp — story first, legend later.",
    benefits: [
      "Lowest-priced collector edition",
      "Full comic reader access",
      "Treydora & Oshar universe access",
    ],
    ctaDetails: "View Details",
    ctaBuy: "Edition Coming Soon",
    tabIds: ["all", "nft_comics"],
  },
];

/** Credit-card storefront copy for Genesis collector edition cards (not wallet mint). */
export const COLLECTOR_EDITION_PURCHASE_BLURB =
  "Digital collector edition with protected comic reader access. Wallet claim available after purchase.";

/** Pack omitted dedicated Koraxos tile — featured art reused for Koraxos row. */
export const LIBRARY_UPCOMING = [
  {
    id: "koraxos",
    title: "Koraxos",
    imageFile: "04_library_featured_display.png",
    teaser: "The first legend carved into Oshar stone — ascent, exile, and the blade that remembers.",
  },
  {
    id: "tara",
    title: "Tara",
    imageFile: "06_tara_placeholder.png",
    teaser: "Whispers in the archive stacks — truths half-spoken, futures half-written.",
  },
  {
    id: "bellon",
    title: "Bellon",
    imageFile: "07_bellon_placeholder.png",
    teaser: "Faith, fracture, and the cities below the storm — when the old wards fail.",
  },
  {
    id: "crooks",
    title: "Crooks",
    imageFile: "08_crooks_placeholder.png",
    teaser: "Shadow lanes and ledger ghosts — every deal has teeth; every smile has a price.",
  },
];

export const LIBRARY_AI_PROMPTS = [
  "Tell me about Koraxos",
  "What is Oshar?",
  "Who is Tara?",
  "Give me spoiler-free lore",
];

/** Comic reader pane 3 — Storyteller card (moved from storefront; no live model in Phase 2). */
export const READER_STORYTELLER_IMAGE_FILE = "03_ai_storyteller_oracle.png";

export const READER_STORYTELLER_TITLE = "Lore Terminal";

export const READER_STORYTELLER_INTRO =
  "Ask about the story, characters, lore, and meaning behind this issue.";

export const READER_STORYTELLER_PROMPTS = [
  "Tell me about Koraxos",
  "What is Oshar?",
  "Give me spoiler-free lore",
  "Explain this chapter",
];

/** Lore Terminal — optional style shortcuts (sends experienceHint to the ask API). */
export const READER_STORYTELLER_MODE_CHIPS = [
  {
    label: "Tell me a story",
    hint: "story",
    prompt: "Tell me a short in-world story inspired by this issue.",
  },
  {
    label: "Give me the lore",
    hint: "lore",
    prompt: "Give me the core lore for what I have unlocked here.",
  },
  {
    label: "Explain the meaning",
    hint: "meaning",
    prompt: "Explain the deeper meaning of this issue — the wound beneath the scene, the omen in the silence.",
  },
];
